import { defineStore } from 'pinia';
import ActivitiesAPI from 'dashboard/api/activities';
import { throwErrorMessage } from 'dashboard/store/utils/api';

export const useActivitiesStore = defineStore('activities', {
  state: () => ({
    records: [],
    timeline: [],
    pagination: null,
    uiFlags: {
      fetchingList: false,
      fetchingTimeline: false,
      creating: false,
    },
  }),

  getters: {
    getActivities: state => state.records,
    getTimeline: state => state.timeline,
    getPagination: state => state.pagination,
    getUIFlags: state => state.uiFlags,
    getActivitiesByType: state => type => state.records.filter(a => a.activityType === type),
  },

  actions: {
    setUIFlag(flags) {
      this.uiFlags = { ...this.uiFlags, ...flags };
    },

    async get(params = {}) {
      this.setUIFlag({ fetchingList: true });
      try {
        const { data } = await ActivitiesAPI.get(params);
        this.records = data;
        return data;
      } catch (error) {
        throwErrorMessage(error);
        return [];
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async fetchTimeline(activitableType, activitableId, params = {}) {
      this.setUIFlag({ fetchingTimeline: true });
      try {
        const { data } = await ActivitiesAPI.timeline(activitableType, activitableId, params);
        this.timeline = data.activities;
        this.pagination = data.pagination;
        return data;
      } catch (error) {
        throwErrorMessage(error);
        return { activities: [], pagination: null };
      } finally {
        this.setUIFlag({ fetchingTimeline: false });
      }
    },

    async create(activityData) {
      this.setUIFlag({ creating: true });
      try {
        const { data } = await ActivitiesAPI.create(activityData);
        this.records.unshift(data);
        return data;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ creating: false });
      }
    },

    async createNote(activitableType, activitableId, content) {
      this.setUIFlag({ creating: true });
      try {
        const { data } = await ActivitiesAPI.createNote(activitableType, activitableId, content);
        this.timeline.unshift(data);
        return data;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ creating: false });
      }
    },

    async logCall(activitableType, activitableId, content, duration = null) {
      this.setUIFlag({ creating: true });
      try {
        const { data } = await ActivitiesAPI.logCall(activitableType, activitableId, content, duration);
        this.timeline.unshift(data);
        return data;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ creating: false });
      }
    },

    addActivityToTimeline(activity) {
      this.timeline.unshift(activity);
    },

    clearRecords() {
      this.records = [];
      this.timeline = [];
      this.pagination = null;
    },

    clearTimeline() {
      this.timeline = [];
      this.pagination = null;
    },
  },
});
